import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Button } from "~/components/Button"
import { GlobalNav } from "~/components/GlobalNav"
import { TokenPanel } from "~/components/TokenPanel"
import { VotePanel } from "~/components/VotePanel"
import { useAccount } from "~/hooks/useAccount"
import { formatLargeBalance } from "~/utils/formatLargeBalance"
import { formatProposalDisplayId } from "~/utils/github"
import { MarkdownContent } from "~/components/MarkdownContent"
import { useGameData, type GameData } from "~/hooks/useGameData"
import { useProposalDetails } from "~/hooks/useProposalDetails"
import { useVote } from "~/hooks/useVote"
import { useUserVote } from "~/hooks/useUserVote"
import type { DetailedProposal, ProposalVoteHistory } from "~/types/api"
import { WorkItem } from "~/components/WorkItem"
import { useDagProgram } from "~/hooks/useDagProgram"
import { PublicKey } from "@solana/web3.js"
import { BN } from "@coral-xyz/anchor"
import toast from 'react-hot-toast'
import { useLightClient } from "~/hooks/useLightClient"
import { useWorkCreation } from "~/hooks/useWorkCreation"
import { api } from "~/services/api"

export default function TaskPage() {
  const { gameId, taskId } = useParams<{ gameId: string, taskId: string }>()
  const { game, isLoading: isGameLoading, error: gameError } = useGameData({ gameId: gameId! })
  const { proposal, isLoading: isProposalLoading, error: proposalError, fetchProposal } = useProposalDetails({ proposalId: taskId! })
  const { account } = useAccount()
  const { getProjectPda, getUserLockPda, program } = useDagProgram()
  const { client } = useLightClient()
  const { voteOnProposal, isLoading: isVotingAny, error: voteError } = useVote()
  const [activeVote, setActiveVote] = useState<'for' | 'against' | null>(null)
  const [isCheckingVote, setIsCheckingVote] = useState(false)
  const [userVoteError, setUserVoteError] = useState<string | null>(null)
  const [userVoteHistory, setUserVoteHistory] = useState<ProposalVoteHistory | null>(null)

  // Check if user has already voted when component mounts
  useEffect(() => {
    const checkUserVote = async () => {
      if (!program || !account?.address || !proposal || !client) return;

      try {
        setIsCheckingVote(true);
        setUserVoteError(null);

        const walletPubkey = new PublicKey(account.address);
        const proposalPdaKey = new PublicKey(proposal.pda);

        // Derive the vote address
        const voteAddress = await client.deriveAddressWithSeed([
          Buffer.from("prop_vote"),
          walletPubkey.toBuffer(),
          proposalPdaKey.toBuffer(),
        ]);

        // Try to fetch the compressed vote account - handle errors gracefully
        let onChainVote = null;
        try {
          const voteAccount = await client.rpc.getCompressedAccount(
            client.bufferToBN254Scalar(voteAddress.address.toBuffer())
          );

          if (voteAccount && voteAccount.data) {
            // Parse the vote data using the program's coder
            const voteData = program.coder.types.decode('proposalVote', voteAccount.data.data);
            console.log("Found existing on-chain vote:", voteData);
            onChainVote = voteData;
          }
        } catch (compressedAccountError) {
          // This is expected if the user hasn't voted yet - the account doesn't exist
          console.log("No compressed vote account found (user likely hasn't voted yet):", compressedAccountError);
          // Don't set this as an error since it's expected behavior
        }

        // Fetch user's vote history from server
        let serverVoteHistory = null;
        try {
          const voteHistory = await api.proposals.getVoteHistory(proposal.proposalId);
          setUserVoteHistory(voteHistory);
          serverVoteHistory = voteHistory;
          console.log("Found server vote history:", voteHistory);
        } catch (error) {
          // If no vote history found (404), that's expected for users who haven't voted
          if (error instanceof Error && error.message.includes('404')) {
            setUserVoteHistory(null);
            console.log("No server vote history found (user hasn't voted)");
          } else {
            console.error("Error fetching vote history:", error);
          }
        }

        // Check for discrepancy: on-chain vote exists but server doesn't have it
        if (onChainVote && !serverVoteHistory) {
          console.log("Discrepancy detected: on-chain vote exists but server doesn't have it. Syncing...");
          
          try {
            // Determine vote direction from on-chain data
            // Assuming the vote data structure has fields like voteYes or similar
            const voteDirection = onChainVote.voteYes ? 'for' : 'against';
            const voteType = 'approve'; // Assuming this is an approve vote, adjust if needed
            
            // Sync the vote to the server
            await api.proposals.vote(proposal.proposalId, {
              voteType,
              voteDirection
            });
            
            console.log("Successfully synced on-chain vote to server");
            
            // Refresh the server vote history after syncing
            try {
              const updatedVoteHistory = await api.proposals.getVoteHistory(proposal.proposalId);
              setUserVoteHistory(updatedVoteHistory);
              console.log("Updated server vote history after sync:", updatedVoteHistory);
            } catch (refreshError) {
              console.error("Error refreshing vote history after sync:", refreshError);
            }
            
          } catch (syncError) {
            console.error("Failed to sync on-chain vote to server:", syncError);
            // Don't throw here, just log the error - the user can still see their on-chain vote
          }
        }

      } catch (error) {
        console.error("Error checking user vote:", error);
        setUserVoteError(error instanceof Error ? error.message : "Failed to check vote status");
      } finally {
        setIsCheckingVote(false);
      }
    };

    checkUserVote();
  }, [program, account?.address, proposal, client]);

  useEffect(() => {
    fetchProposal()
  }, [fetchProposal])

  const handleVote = async (voteDirection: "for" | "against") => {
    if (!program || !account.address || !game || !proposal) {
      throw new Error("Program, account, game or proposal not initialized");
    }

    try {
      const projectPda = getProjectPda(game.token.address);
      
      // Call voteOnProposal with the correct parameters
      await voteOnProposal({
        projectPda,
        proposalId: proposal.proposalId,
        proposalPda: proposal.pda,
        voteDirection,
      });

      // Refresh data after successful vote
      await fetchProposal();
      toast.success('Vote submitted successfully');
    } catch (error) {
      console.error("Failed to vote:", error);
      if (error instanceof Error && error.message.includes("already voted")) {
        toast.error("You have already voted on this proposal");
      } else {
        toast.error(error instanceof Error ? error.message : "Failed to vote");
      }
      throw error;
    }
  };

  const isLoading = isGameLoading || isProposalLoading || isCheckingVote
  const error = gameError || proposalError || voteError || userVoteError

  if (isLoading || !game) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-red-500">Error: {typeof error === 'string' ? error : error.message}</p>
        </div>
      </div>
    )
  }

  if (!proposal) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Task not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <GlobalNav />

      <div className="flex gap-6 pt-6 px-10">
        <div className="flex-1 flex flex-col gap-6">
          <TaskDetail 
            game={game} 
            proposal={proposal}
            onVote={handleVote}
            activeVote={activeVote}
            isVotingAny={isVotingAny}
            onRefresh={fetchProposal}
          />
        </div>

        <div className="sticky top-0 flex flex-col gap-6 w-[320px]">
          <TokenPanel game={game} />
          <VotePanel game={game} />
        </div>
      </div>
    </div>
  )
}

interface TaskDetailProps {
  game: GameData;
  proposal: DetailedProposal;
  onVote: (voteDirection: 'for' | 'against') => Promise<any>;
  activeVote: 'for' | 'against' | null;
  isVotingAny: boolean;
  onRefresh: () => Promise<void>;
}

const TaskDetail = ({ game, proposal, onVote, activeVote, isVotingAny, onRefresh }: TaskDetailProps) => {
  const { isAuthenticated, account } = useAccount()
  const { getProjectPda } = useDagProgram()
  const { createWork, isLoading: isCreatingWork, error: workCreationError } = useWorkCreation()
  
  // Get user's current vote
  const projectPda = getProjectPda(game.token.address)
  const { userVote, isLoading: isLoadingVote, refetchUserVote } = useUserVote({
    proposalPda: proposal.pda,
    projectPda
  })

  const isVoting = (direction: 'for' | 'against') => {
    return activeVote === direction
  }

  const isDisabled = () => {
    return !isAuthenticated || isVotingAny
  }

  const handleVoteWithRefresh = async (voteDirection: 'for' | 'against') => {
    await onVote(voteDirection)
    // Refresh user vote after successful vote
    await refetchUserVote()
  }

  const handleContribute = async () => {
    if (!account?.address || !isAuthenticated) {
      toast.error('Please connect your wallet and sign in')
      return
    }

    try {
      await createWork({
        proposalId: proposal.proposalId,
        proposalPda: proposal.pda,
        tokenAddress: game.token.address
      })

      toast.success('Work item created successfully!')
      await onRefresh() // Refresh the proposal to show the new work item
    } catch (error) {
      console.error('Failed to create work:', error)
      if (error instanceof Error && error.message.includes('GitHub')) {
        toast.error('GitHub account must be connected to contribute')
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to create work item')
      }
    }
  }

  // Format the display ID using GitHub issue ID if available
  const displayId = formatProposalDisplayId(proposal.proposalId, proposal.githubIssueUrl)

  // Check if proposal is no longer in voting status (can accept contributions)
  const canContribute = proposal.status === 'active'

  return (
    <div className="flex flex-col gap-4 px-4 py-3 rounded-lg border border-solid border-[#212425]">
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center relative gap-4">
          <p className="text-base font-medium text-white">
            {proposal.title} {displayId}
          </p>
          <div className="flex justify-center items-center gap-1">
            <div className="flex items-center relative gap-1">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 relative"
                preserveAspectRatio="none"
              >
                <path
                  d="M4.44453 5.56138C5.42637 5.56138 6.2223 4.76415 6.2223 3.78072C6.2223 2.79729 5.42637 2.00006 4.44453 2.00006C3.46269 2.00006 2.66675 2.79729 2.66675 3.78072C2.66675 4.76415 3.46269 5.56138 4.44453 5.56138ZM4.44453 5.56138V13.3778M11.5556 9.81652C12.5375 9.81652 13.3334 10.6137 13.3334 11.5972C13.3334 12.5806 12.5375 13.3778 11.5556 13.3778C10.5738 13.3778 9.77786 12.5806 9.77786 11.5972C9.77786 10.6137 10.5738 9.81652 11.5556 9.81652ZM11.5556 9.81652V5.58511C11.5556 4.60327 10.7597 3.80733 9.77786 3.80733H8.71119"
                  stroke="#6B6D6E"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13px] font-medium text-[#6b6d6e]">
                {proposal.works.length}
              </p>
            </div>
            <div className="flex items-center relative gap-2.5 py-[3px]">
              <div className="w-px bg-[#2d3031]" />
            </div>
            <div className="flex items-center relative gap-1">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 relative"
                preserveAspectRatio="none"
              >
                <path
                  d="M7.99992 5.46665V3.33331M7.99992 12.6666V10.5333M10.5333 7.99998H12.6666M3.33325 7.99998H5.46659M9.79142 6.20873L11.2999 4.70024M4.69976 11.2999L6.20825 9.79139M9.79142 9.79123L11.2999 11.2997M4.69976 4.70008L6.20825 6.20857"
                  stroke="#6B6D6E"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13px] font-medium text-[#6b6d6e]">
                {proposal.approveVotesFor + proposal.approveVotesAgainst}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex justify-end items-center relative gap-4">
            <p className="text-[13px] font-medium text-[#09d26a]">
              (Earn {formatLargeBalance(game.token.devPool * (proposal.bountyBps / 10000))} {game.token.symbol})
            </p>
          </div>
          
          {/* Contribute Button - only show for active proposals */}
          {canContribute && (
            <Button
              onClick={handleContribute}
              disabled={!isAuthenticated || isCreatingWork}
              loading={isCreatingWork}
              size="sm"
              type="primary"
            >
              Contribute
            </Button>
          )}
          
          {proposal.githubIssueUrl && (
            <div className="flex items-center gap-2">
              <a 
                target="_blank" 
                rel="noreferrer" 
                href={proposal.githubIssueUrl} 
                className="flex justify-center items-center relative gap-1 px-3 py-2 rounded bg-[#212425]"
              >
                <svg
                  width={16}
                  height={17}
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M6.8679 9.06611C7.11101 9.39112 7.42118 9.66004 7.77736 9.85464C8.13354 10.0492 8.52741 10.165 8.93225 10.194C9.33709 10.223 9.74343 10.1645 10.1237 10.0227C10.504 9.88083 10.8493 9.65885 11.1363 9.3718L12.8345 7.67351C13.3501 7.13968 13.6354 6.4247 13.629 5.68256C13.6225 4.94042 13.3249 4.2305 12.8001 3.7057C12.2753 3.18091 11.5653 2.88323 10.8232 2.87679C10.0811 2.87034 9.36608 3.15563 8.83225 3.67123L7.85856 4.63925M9.13228 7.93392C8.88917 7.60891 8.579 7.33998 8.22282 7.14538C7.86664 6.95078 7.47277 6.83506 7.06793 6.80607C6.66309 6.77707 6.25675 6.83549 5.87647 6.97734C5.49619 7.1192 5.15087 7.34118 4.86392 7.62823L3.16564 9.32651C2.65005 9.86034 2.36475 10.5753 2.3712 11.3175C2.37765 12.0596 2.67533 12.7695 3.20012 13.2943C3.72491 13.8191 4.43483 14.1168 5.17697 14.1232C5.91911 14.1297 6.6341 13.8444 7.16793 13.3288L8.13595 12.3608"
                    stroke="white"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-[13px] font-semibold text-white">
                  Github
                </p>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
          proposal.status === 'voting' 
            ? 'bg-yellow-900/20 text-yellow-400 border border-yellow-500/50'
            : proposal.status === 'active'
            ? 'bg-green-900/20 text-green-400 border border-green-500/50'
            : proposal.status === 'rejected'
            ? 'bg-red-900/20 text-red-400 border border-red-500/50'
            : 'bg-blue-900/20 text-blue-400 border border-blue-500/50'
        }`}>
          {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
        </span>
      </div>

      <MarkdownContent 
        content={proposal.description}
        className="p-4 rounded-lg bg-[#212425]/50"
      />

      {/* Current User Vote Display */}
      {isAuthenticated && account?.address && userVote && (
        <div className="p-3 rounded-lg bg-blue-900/20 border border-blue-500/50">
          <div className="flex items-center gap-2">
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-blue-400"
            >
              <path
                d="M8 1L10.09 5.26L15 6L11 9.74L11.91 14.74L8 12.77L4.09 14.74L5 9.74L1 6L5.91 5.26L8 1Z"
                fill="currentColor"
              />
            </svg>
            <span className="text-sm font-medium text-blue-400">
              You voted {userVote.voteDirection === 'for' ? 'to approve' : 'to reject'} this proposal
            </span>
            <span className="text-xs text-blue-300">
              (Power: {formatLargeBalance(userVote.votePower)})
            </span>
          </div>
        </div>
      )}

      {/* Voting Section */}
      {proposal.status === 'voting' && (
        <div className="flex flex-col gap-4">
          {/* Approve Votes */}
          <div className="p-4 rounded-lg bg-white/5 border border-solid border-[#212425]">
            <div className="flex items-center gap-6">
              <div className="flex flex-col">
                <h3 className="text-lg font-medium text-white whitespace-nowrap">Add to taskboard</h3>
                <p className="text-sm text-[#9c9e9e]">If this proposal is approved, contributors can earn {game.token.symbol} by solving this task</p>
              </div>
              <div className="flex items-center justify-end gap-4 flex-1">
                <div className="flex flex-col gap-2 max-w-[160px] w-full">
                  <div className="flex justify-between items-center text-sm">
                    <div className="text-[#60e25e] whitespace-nowrap">
                      {proposal.approveVotesFor} ({proposal.approveVotesFor + proposal.approveVotesAgainst > 0 
                        ? Math.round((proposal.approveVotesFor / (proposal.approveVotesFor + proposal.approveVotesAgainst)) * 100) 
                        : 0}%)
                    </div>
                    <div className="text-[#ff4949] whitespace-nowrap">
                      ({proposal.approveVotesFor + proposal.approveVotesAgainst > 0 
                        ? Math.round((proposal.approveVotesAgainst / (proposal.approveVotesFor + proposal.approveVotesAgainst)) * 100)
                        : 0}%) {proposal.approveVotesAgainst}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="h-2 rounded-full bg-[#212425] overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ 
                          width: `${proposal.approveVotesFor + proposal.approveVotesAgainst > 0 
                            ? (proposal.approveVotesFor / (proposal.approveVotesFor + proposal.approveVotesAgainst)) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleVoteWithRefresh('for')}
                    disabled={isDisabled() || userVote !== null}
                    loading={isVoting('for')}
                    size="sm"
                    type={userVote?.voteDirection === 'for' ? 'primary' : 'primary'}
                    className={userVote?.voteDirection === 'for' ? 'opacity-75' : ''}
                  >
                    {userVote?.voteDirection === 'for' ? 'Approved' : 'Approve'}
                  </Button>
                  <Button
                    onClick={() => handleVoteWithRefresh('against')}
                    disabled={isDisabled() || userVote !== null}
                    loading={isVoting('against')}
                    size="sm"
                    type={userVote?.voteDirection === 'against' ? 'secondary' : 'secondary'}
                    className={userVote?.voteDirection === 'against' ? 'opacity-75' : ''}
                  >
                    {userVote?.voteDirection === 'against' ? 'Rejected' : 'Reject'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Works Section */}
      {(proposal.status === 'active' || proposal.status === 'workItemApproved' || proposal.status === 'completed') && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold text-white">Work Submissions</h3>
            <span className="px-2 py-1 text-sm font-medium bg-blue-900/20 text-blue-400 rounded">
              {proposal.works.length}
            </span>
            {proposal.status === 'active' && (
              <span className="px-2 py-1 text-xs font-medium bg-green-900/20 text-green-400 rounded">
                Accepting Contributions
              </span>
            )}
          </div>
          <div className="flex flex-col gap-3">
            {proposal.works.map((work) => (
              <WorkItem key={work.workId} workId={work.workId} />
            ))}
            {proposal.works.length === 0 && (
              <div className="p-4 rounded-lg bg-white/5 border border-solid border-[#212425]">
                <p className="text-[#9c9e9e]">
                  {proposal.status === 'active' 
                    ? 'No work items submitted yet. Click "Contribute" to submit your work!'
                    : 'No work items were submitted for this task.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
} 